import React, { useContext, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { Helmet } from 'react-helmet';
import DOMPurify from 'dompurify';
import { Container } from 'reactstrap';
import { useParams, useSearchParams } from 'react-router-dom';
import { IntercomProvider } from 'react-use-intercom';
import Header from './components/Header';
import CampaignHeader from './components/CampaignHeader';
import Footer from './components/Footer';
import ArticlesList from './components/ArticlesList';
import { CampaignContext } from '../../context/CampaignWrapper';
import MainSkeleton from './components/skeletons/MainSkeleton';
import CartModal from './components/CartModal';
import ArticleModal from './components/ArticleModal';
import ScoreboardModal from './components/ScoreboardModal';
import { getCloudinaryUrl, isRegularArticle } from '../../utils/helpers';
import { Article, useSellerGroupsQuery } from '../../generated/graphql';
import { OrderContext } from '../../context/OrderContext';
import { BuilderHeader } from '../../components/BuilderHeader';

const { REACT_APP_INTERCOM_APP_ID } = process.env;

const MainContainer = () => {
  const { slug } = useParams();
  const {
    campaign: {
      articles: allArticles = [], openGraphTags = {}, color = '', name = '', headerImgUrl = '', isSellingEnabled = true,
      textConfig: { sellingDisabled = '' } = {},
    } = {},
    loading,
  } = useContext(CampaignContext);
  const { data: sellerGroupsData } = useSellerGroupsQuery({ variables: { slug: slug || '' } });
  const sellerGroups = sellerGroupsData?.campaignBySlug?.sellerGroups || [];
  const articles = allArticles.filter(isRegularArticle);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const queryParamScoreboard = searchParams.get('scoreboard');
  const [isScoreboardOpen, setIsScoreboardOpen] = useState(queryParamScoreboard === 'true');
  const [selectedArticleId, setSelectedArticleId] = useState<Article['_id']>();
  const { sellerGroupCode } = useParams();
  const { setOrder } = useContext(OrderContext);
  const sellerGroup = sellerGroups.find((sg) => sg.code === sellerGroupCode);
  useEffect(() => {
    if (sellerGroup?._id) setOrder((o) => ({ ...(o || {}), sellerGroupId: sellerGroup?._id }));
  }, [sellerGroup?._id, setOrder]);

  const hideImages = articles.every((a) => !a.imageUrls?.length);
  const articleVariants = (articles || []).reduce<Article[][]>((arr, article) => {
    if (!article.groupId) return [...arr, [article]];
    const existingArray = arr.find((array) => array.some((a) => a.groupId === article.groupId));
    if (existingArray) existingArray.push(article);
    else arr.push([article]);
    return arr;
  }, []);

  const selectedArticle = articles.find((a) => a._id === selectedArticleId);

  const openCartModal = () => setIsCartOpen(true);

  const openArticleModal = (articleId: string) => setSelectedArticleId(articleId);

  const closeArticleModal = () => setSelectedArticleId('');

  const openScoreboardModal = () => setIsScoreboardOpen(true);

  useEffect(() => {
    if (isCartOpen || selectedArticleId) {
      toast.dismiss();
    }
  }, [isCartOpen, selectedArticleId]);

  if (loading) return <MainSkeleton />;

  const ogImageUrl = (openGraphTags && openGraphTags.imageUrl) || headerImgUrl;

  const getFavicon = (width: number) => {
    if (color && color.startsWith('#')) {
      const buildImage = `l_text:Arial_100_bold:%E2%80%A2,co_rgb:${color.replace('#', '')}`;
      return `https://res.cloudinary.com/crowdselling/image/upload/${buildImage}/c_scale,w_${width}/favicons/favicon_rzz0fw.png`;
    }

    return `/favicon-${width}x${width}.png`;
  };

  return (
    <>
      <Helmet>
        <link rel="icon" type="image/png" sizes="32x32" href={getFavicon(32)} />
        <link rel="icon" type="image/png" sizes="16x16" href={getFavicon(16)} />
        <title>
          {name}
          {' '}
          |
          {' '}
          Trooper Webshops
        </title>
        {color && <meta name="theme-color" content={color} />}
        {/* Note that this won't be necessary 99% of the cases, as social bots
        are redirected to /netlify/edge-functions/social-sharing.ts */}
        {openGraphTags && openGraphTags.title && <meta property="og:title" content={openGraphTags.title} />}
        {ogImageUrl && (
          <meta
            property="og:image"
            content={getCloudinaryUrl(ogImageUrl, ['c_limit', 'f_jpg', 'h_1200', 'q_auto:good', 'w_1200'])}
          />
        )}
        {openGraphTags && openGraphTags.description && <meta property="og:description" content={openGraphTags.description} />}
        <meta property="og:type" content="website" />
      </Helmet>
      <div className="min-vh-100 d-flex flex-column">
        {slug === 'demo' && (
          <IntercomProvider appId={REACT_APP_INTERCOM_APP_ID || ''} autoBoot>
            <BuilderHeader />
          </IntercomProvider>
        )}
        <Header openCartModal={openCartModal} />
        <CampaignHeader openScoreboardModal={openScoreboardModal} />
        <Container className="my-5">
          {isSellingEnabled ? (
            <ArticlesList
              allArticleVariants={articleVariants}
              openArticleModal={openArticleModal}
              hideImages={hideImages}
            />
          ) : <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(sellingDisabled) }} />}
        </Container>
        <Footer />
      </div>
      <CartModal
        isOpen={isCartOpen}
        setIsOpen={setIsCartOpen}
      />
      {isScoreboardOpen && (
        <ScoreboardModal
          isOpen={isScoreboardOpen}
          setIsOpen={setIsScoreboardOpen}
          onClose={() => {
            if (searchParams.has('scoreboard')) {
              searchParams.delete('scoreboard');
              setSearchParams(searchParams);
            }
          }}
        />
      )}
      {selectedArticle && (
        <ArticleModal
          articleVariants={(selectedArticle || {}).groupId
            ? articleVariants.find(([a]) => a.groupId === (selectedArticle || {}).groupId) || [selectedArticle]
            : [selectedArticle]}
          article={selectedArticle}
          setArticleId={setSelectedArticleId}
          closeArticleModal={closeArticleModal}
        />
      )}
    </>
  );
};

export default MainContainer;
